'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import '../(landing)/home.css';

export default function LoginPage() {
  const router = useRouter();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /* ─── Particle canvas animation ─── */
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const setSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();

    type P = { x: number; y: number; v: number; o: number };
    let ps: P[] = [];
    let raf = 0;

    const make = (): P => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      v: Math.random() * 0.3 + 0.08,
      o: Math.random() * 0.5 + 0.3,
    });

    const init = () => {
      ps = [];
      const count = Math.floor((canvas.width * canvas.height) / 9000);
      for (let i = 0; i < count; i++) ps.push(make());
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ps.forEach((p) => {
        p.y -= p.v;
        if (p.y < 0) {
          p.x = Math.random() * canvas.width;
          p.y = canvas.height + Math.random() * 40;
          p.v = Math.random() * 0.3 + 0.08;
          p.o = Math.random() * 0.5 + 0.3;
        }
        const colors = [
          [163,230,53],  // lime-400
          [132,204,22],  // lime-500
          [101,163,13],  // lime-600
        ];
        const c = colors[Math.floor(Math.random() * colors.length)];
        ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${p.o})`;
        ctx.fillRect(p.x, p.y, 1, 2.5);
      });
      raf = requestAnimationFrame(draw);
    };

    const onResize = () => {
      setSize();
      init();
    };

    window.addEventListener('resize', onResize);
    init();
    raf = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  /* ─── Spotlight mouse tracking ─── */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const cards = document.querySelectorAll<HTMLElement>('.spotlight-card');
      cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  /* ─── Auth handler ─── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login'
        ? { email, password, rememberMe }
        : { email, username, name, password };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Terjadi kesalahan');
        return;
      }

      // Handle pending approval for new registrations
      if (data.pendingApproval) {
        setPendingApproval(true);
        return;
      }

      router.push('/dashboard');
      router.refresh();
    } catch {
      setError('Gagal terhubung ke server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="fixed inset-0 bg-zinc-950 text-zinc-50 font-sans">
      <style>{`
        /* ─── Animated accent grid lines ─── */
        .accent-lines{position:absolute;inset:0;pointer-events:none;opacity:.7}
        .hline,.vline{position:absolute;background:#27272a;will-change:transform,opacity}
        .hline{left:0;right:0;height:1px;transform:scaleX(0);transform-origin:50% 50%;animation:drawX .8s cubic-bezier(.22,.61,.36,1) forwards}
        .vline{top:0;bottom:0;width:1px;transform:scaleY(0);transform-origin:50% 0%;animation:drawY .9s cubic-bezier(.22,.61,.36,1) forwards}
        .hline:nth-child(1){top:18%;animation-delay:.12s}
        .hline:nth-child(2){top:50%;animation-delay:.22s}
        .hline:nth-child(3){top:82%;animation-delay:.32s}
        .vline:nth-child(4){left:22%;animation-delay:.42s}
        .vline:nth-child(5){left:50%;animation-delay:.54s}
        .vline:nth-child(6){left:78%;animation-delay:.66s}
        .hline::after,.vline::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(250,250,250,.24),transparent);opacity:0;animation:shimmer .9s ease-out forwards}
        .hline:nth-child(1)::after{animation-delay:.12s}
        .hline:nth-child(2)::after{animation-delay:.22s}
        .hline:nth-child(3)::after{animation-delay:.32s}
        .vline:nth-child(4)::after{animation-delay:.42s}
        .vline:nth-child(5)::after{animation-delay:.54s}
        .vline:nth-child(6)::after{animation-delay:.66s}
        @keyframes drawX{0%{transform:scaleX(0);opacity:0}60%{opacity:.95}100%{transform:scaleX(1);opacity:.7}}
        @keyframes drawY{0%{transform:scaleY(0);opacity:0}60%{opacity:.95}100%{transform:scaleY(1);opacity:.7}}
        @keyframes shimmer{0%{opacity:0}35%{opacity:.25}100%{opacity:0}}

        /* ─── Card fade-up entrance ─── */
        .card-animate {
          opacity: 0;
          transform: translateY(24px);
          animation: fadeUp 0.8s cubic-bezier(.22,.61,.36,1) 0.4s forwards;
        }
        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        /* ─── Input focus glow ─── */
        .login-input:focus {
          border-color: #14b8a6;
          box-shadow: 0 0 0 3px rgba(20,184,166,0.15);
        }

        /* ─── Button hover lift ─── */
        .login-btn:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(255,255,255,0.15);
        }
        .login-btn:not(:disabled):active {
          transform: translateY(0);
        }
      `}</style>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at var(--gx, 50%) var(--gy, 30%), rgba(163,230,53,0.06), transparent 60%)',
        animation: 'gradientDrift 12s ease-in-out infinite alternate',
      }} />
      <style>{`
        @keyframes gradientDrift {
          0%   { --gx: 30%; --gy: 20%; }
          25%  { --gx: 70%; --gy: 35%; }
          50%  { --gx: 40%; --gy: 60%; }
          75%  { --gx: 65%; --gy: 25%; }
          100% { --gx: 35%; --gy: 45%; }
        }
        @property --gx { syntax: '<percentage>'; inherits: false; initial-value: 50%; }
        @property --gy { syntax: '<percentage>'; inherits: false; initial-value: 30%; }
      `}</style>

      {/* Animated accent lines */}
      <div className="accent-lines">
        <div className="hline" />
        <div className="hline" />
        <div className="hline" />
        <div className="vline" />
        <div className="vline" />
        <div className="vline" />
      </div>

      {/* Particles */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-70 pointer-events-none"
        style={{ mixBlendMode: 'screen' }}
      />

      {/* Header bar */}
      <header className="absolute left-0 right-0 top-0 flex items-center justify-between px-6 py-4"
        style={{ borderBottom: '1px solid rgba(63,63,70,0.2)' }}>
        <Link href="/" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="STUDO.ID" className="h-8 w-8 object-contain" />
          <span className="text-white font-bold text-lg tracking-tight">
            STUDO<span style={{ color: '#a3e635' }}>.ID</span>
          </span>
        </Link>
      </header>

      {/* ─── Centered Login Card ─── */}
      <div className="h-full w-full flex items-center justify-center px-4">
        <div className="card-animate spotlight-card w-full max-w-sm rounded-xl p-[1px]"
          style={{
            background: 'radial-gradient(circle, rgb(24,24,27) 0%, rgb(0,0,0) 100%)',
          }}>
          <div className="rounded-[11px] p-8 h-full w-full"
            style={{
              background: 'rgba(24,24,27,0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}>

          {/* ─── Pending Approval Success Card ─── */}
          {pendingApproval ? (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(163,230,53,0.1)', border: '2px solid rgba(163,230,53,0.3)' }}>
                  <svg className="w-8 h-8 text-lime-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-zinc-50 mb-2">Pendaftaran Berhasil!</h2>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Akun Anda telah dibuat dan sedang menunggu persetujuan administrator. Anda akan dapat masuk setelah akun disetujui.
              </p>
              <button
                onClick={() => { setPendingApproval(false); setMode('login'); setError(''); setPassword(''); }}
                className="w-full h-10 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  background: '#f4f4f5',
                  color: '#18181b',
                }}
              >
                Kembali ke Login
              </button>
            </div>
          ) : (
          <>

          {/* Logo studo.id */}
          <div className="flex flex-col items-center mb-5 gap-0.5">
            <img
              src="/logo.png"
              alt="kreatif.studo.id"
              className="h-12 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' }}
            />
            <span className="text-sm tracking-wide text-zinc-400" style={{ letterSpacing: '0.05em' }}>
              kreatif.studo.id
            </span>
          </div>

          {/* Card Header */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">
              {mode === 'login' ? 'Selamat datang' : 'Buat akun baru'}
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              {mode === 'login'
                ? 'Masuk ke dashboard Anda'
                : 'Daftar untuk memulai'}
            </p>
          </div>

          {/* Error alert */}
          {error && (
            <div className="mb-5 px-4 py-3 rounded-md text-sm"
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#f87171',
              }}>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">

            {/* Name (register only) */}
            {mode === 'register' && (
              <div className="flex flex-col gap-2">
                <label htmlFor="login-name" className="text-sm font-medium text-zinc-300">
                  Nama Lengkap
                </label>
                <input
                  id="login-name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Nama lengkap"
                  required
                  className="login-input w-full h-10 px-4 rounded-md text-sm text-zinc-50 placeholder-zinc-600 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(9,9,11,0.8)',
                    border: '1px solid rgba(63,63,70,0.2)',
                  }}
                />
              </div>
            )}

            {/* Username (register only) */}
            {mode === 'register' && (
              <div className="flex flex-col gap-2">
                <label htmlFor="login-username" className="text-sm font-medium text-zinc-300">
                  Username
                </label>
                <input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="username_anda"
                  required
                  minLength={3}
                  maxLength={20}
                  className="login-input w-full h-10 px-4 rounded-md text-sm text-zinc-50 placeholder-zinc-600 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(9,9,11,0.8)',
                    border: '1px solid rgba(63,63,70,0.2)',
                  }}
                />
                <p className="text-[11px] text-zinc-500">Huruf kecil, angka, underscore. Min 3 karakter.</p>
              </div>
            )}

            {/* Email / Identifier */}
            <div className="flex flex-col gap-2">
              <label htmlFor="login-email" className="text-sm font-medium text-zinc-300">
                {mode === 'login' ? 'Email atau Username' : 'Email'}
              </label>
              <input
                id="login-email"
                type={mode === 'login' ? 'text' : 'email'}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={mode === 'login' ? 'email@contoh.com atau username' : 'you@example.com'}
                required
                className="login-input w-full h-10 px-4 rounded-md text-sm text-zinc-50 placeholder-zinc-600 outline-none transition-all duration-200"
                style={{
                  background: 'rgba(9,9,11,0.8)',
                  border: '1px solid rgba(63,63,70,0.2)',
                }}
              />
            </div>

            {/* Password */}
            <div className="flex flex-col gap-2">
              <label htmlFor="login-password" className="text-sm font-medium text-zinc-300">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="login-input w-full h-10 px-4 pr-10 rounded-md text-sm text-zinc-50 placeholder-zinc-600 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(9,9,11,0.8)',
                    border: '1px solid rgba(63,63,70,0.2)',
                  }}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-zinc-400 hover:text-zinc-200 transition-colors"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded accent-zinc-50"
                  style={{
                    accentColor: '#f4f4f5',
                  }}
                />
                <span className="text-sm text-zinc-400">Ingatkan saya</span>
              </label>
              <a href="#" className="text-sm text-zinc-300 hover:text-zinc-100 transition-colors">
                Lupa password?
              </a>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="login-btn w-full h-11 rounded-md text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: loading
                  ? 'rgba(244,244,245,0.4)'
                  : '#f4f4f5',
                color: '#18181b',
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  {mode === 'login' ? 'Masuk' : 'Daftar'}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>


          </form>

          {/* Footer */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="w-full h-10 rounded-md text-sm font-medium transition-all duration-200 hover:opacity-80"
              style={{
                background: 'transparent',
                border: '1px solid rgba(63,63,70,0.2)',
                color: '#a1a1aa',
              }}
            >
              {mode === 'login'
                ? 'Belum punya akun? Daftar'
                : 'Sudah punya akun? Masuk'}
            </button>
          </div>
          </>
          )}
          </div>
        </div>
      </div>
    </section>
  );
}
